import { List, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ScrollableContent } from '@/shared/ui/scrollable-content/ScrollableContent';

import type { DiagramSeriesGroup } from '../../types/DiagramSeries.types';
import { getSelectableSeriesGroups } from '../../utils/diagramSeriesGroups';
import { TimeSeriesChartErrorState } from '../time-series-chart-error-state/TimeSeriesChartErrorState';
import { DiagramSeriesGroupList } from './DiagramSeriesGroupList';
import { SelectorMessage, SelectorRoot } from './DiagramSeriesSelector.style';

export type DiagramSeriesSelectorProps = {
  groups: DiagramSeriesGroup[];
  selectedSeriesIds: string[];
  onSelectedSeriesIdsChange: (seriesIds: string[]) => void;
  getSeriesColor: (seriesId: string) => string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry: () => void;
};

type DiagramSeriesSelectorBodyProps = {
  groups: DiagramSeriesGroup[];
  selectedIds: ReadonlySet<string>;
  seriesCount: number;
  getSeriesColor: (seriesId: string) => string;
  onToggle: (seriesId: string) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

/** The list area for the state of the series metadata: loading, failed, nothing to offer, or the groups. */
function DiagramSeriesSelectorBody({
  groups,
  selectedIds,
  seriesCount,
  getSeriesColor,
  onToggle,
  isLoading,
  isError,
  onRetry,
}: DiagramSeriesSelectorBodyProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <SelectorMessage role="status" aria-live="polite" variant="body2" color="text.secondary">
        {t('timeSeriesChart.channelSelector.loading')}
      </SelectorMessage>
    );
  }

  if (isError) {
    return (
      <TimeSeriesChartErrorState
        message={t('timeSeriesChart.channelSelector.error')}
        isRetrying={false}
        onRetry={onRetry}
        data-qa-element-id="time-series-channel-selector-error"
      />
    );
  }

  if (seriesCount === 0) {
    return (
      <SelectorMessage variant="body2" color="text.secondary">
        {t('timeSeriesChart.channelSelector.noSeries')}
      </SelectorMessage>
    );
  }

  return (
    <List aria-label={t('timeSeriesChart.channelSelector.title')} sx={{ py: 0 }}>
      {groups.map((group) => (
        <DiagramSeriesGroupList
          key={group.id}
          group={group}
          selectedIds={selectedIds}
          getSeriesColor={getSeriesColor}
          onToggle={onToggle}
        />
      ))}
    </List>
  );
}

export function DiagramSeriesSelector({
  groups,
  selectedSeriesIds,
  onSelectedSeriesIdsChange,
  getSeriesColor,
  isLoading = false,
  isError = false,
  onRetry,
}: DiagramSeriesSelectorProps) {
  const { t } = useTranslation();
  const selectedIds = new Set(selectedSeriesIds);
  // A series without data in the chosen window is not offered at all, rather than shown disabled.
  const selectableGroups = getSelectableSeriesGroups(groups);
  const seriesCount = selectableGroups.reduce((count, group) => count + group.series.length, 0);

  function handleToggle(seriesId: string) {
    const nextSeriesIds = selectedIds.has(seriesId)
      ? selectedSeriesIds.filter((selectedId) => selectedId !== seriesId)
      : [...selectedSeriesIds, seriesId];

    onSelectedSeriesIdsChange(nextSeriesIds);
  }

  return (
    <SelectorRoot data-qa-element-id="time-series-channel-selector">
      <ScrollableContent
        header={
          <>
            <Typography variant="h6" component="h2">
              {t('timeSeriesChart.channelSelector.title')}
            </Typography>
            {seriesCount > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-qa-element-id="time-series-channel-selector-count"
              >
                {seriesCount}/{selectedIds.size}
              </Typography>
            )}
          </>
        }
      >
        <DiagramSeriesSelectorBody
          groups={selectableGroups}
          selectedIds={selectedIds}
          seriesCount={seriesCount}
          getSeriesColor={getSeriesColor}
          onToggle={handleToggle}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
        />
      </ScrollableContent>
    </SelectorRoot>
  );
}
